import { createViqApi } from "~/services/viq-api";

const state = () => ({
  // Feature flag
  enabled: false,

  // Conversations
  conversations: [],
  activeConversation: null,
  conversationsLoading: false,

  // Messages within active conversation
  messages: [],
  messagesLoading: false,

  // Lenses
  lenses: [],
  activeLens: null,
  lensesLoading: false,

  // Mode: 'ask' | 'report'
  mode: "ask",

  // Report sub-mode
  reportType: null, // 'board' | 'timeline' | 'gap-analysis' | 'market-entry' | 'compare-markets' | 'expansion'

  // Report state
  reportBriefing: null,
  reportOutline: [],
  reportSections: [],
  reportExecSummary: "",
  reportStep: 0, // 0=type, 1=briefing, 2=outline, 3=streaming, 4=summary

  // Gap analysis
  gapQuestions: [],
  gapResults: null,

  // Timeline
  timelineEvents: [],

  // Expansion
  expansionBaseline: null,
  expansionResults: null,

  // Streaming
  isStreaming: false,
  streamingText: "",
  streamingMeta: null,
  streamController: null,

  // UI state
  sidebarCollapsed: false,
  slideOutOpen: false,
  slideOutArticle: null,
  shareDialogOpen: false,
  shareTarget: null,

  // Artifacts
  artifacts: [],

  // Errors
  error: null,
});

const getters = {
  isEnabled: (state) => state.enabled,
  hasConversations: (state) => state.conversations.length > 0,
  activeConversationId: (state) => state.activeConversation?.id || null,

  conversationsGrouped: (state) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const sevenDaysAgo = new Date(today.getTime() - 7 * 86400000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);

    const groups = {
      today: [],
      yesterday: [],
      last7Days: [],
      last30Days: [],
      older: [],
    };

    state.conversations.forEach((conv) => {
      const date = new Date(conv.updated_at || conv.created_at);
      if (date >= today) {
        groups.today.push(conv);
      } else if (date >= yesterday) {
        groups.yesterday.push(conv);
      } else if (date >= sevenDaysAgo) {
        groups.last7Days.push(conv);
      } else if (date >= thirtyDaysAgo) {
        groups.last30Days.push(conv);
      } else {
        groups.older.push(conv);
      }
    });

    return groups;
  },

  sortedMessages: (state) => {
    return [...state.messages].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  },

  currentStreamContent: (state) => state.streamingText,
  isReportMode: (state) => state.mode === "report",
  isAskMode: (state) => state.mode === "ask",
};

const mutations = {
  SET_ENABLED(state, val) {
    state.enabled = val;
  },

  // Conversations
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations;
  },
  SET_ACTIVE_CONVERSATION(state, conversation) {
    state.activeConversation = conversation;
  },
  ADD_CONVERSATION(state, conversation) {
    state.conversations.unshift(conversation);
  },
  UPDATE_CONVERSATION(state, { id, data }) {
    const idx = state.conversations.findIndex((c) => c.id === id);
    if (idx !== -1) {
      state.conversations.splice(idx, 1, { ...state.conversations[idx], ...data });
    }
    if (state.activeConversation && state.activeConversation.id === id) {
      state.activeConversation = { ...state.activeConversation, ...data };
    }
  },
  REMOVE_CONVERSATION(state, id) {
    state.conversations = state.conversations.filter((c) => c.id !== id);
    if (state.activeConversation && state.activeConversation.id === id) {
      state.activeConversation = null;
      state.messages = [];
    }
  },
  SET_CONVERSATIONS_LOADING(state, val) {
    state.conversationsLoading = val;
  },

  // Messages
  SET_MESSAGES(state, messages) {
    state.messages = messages;
  },
  ADD_MESSAGE(state, message) {
    state.messages.push(message);
  },
  UPDATE_LAST_ASSISTANT_MESSAGE(state, data) {
    const lastIdx = state.messages.length - 1;
    if (lastIdx >= 0 && state.messages[lastIdx].role === "assistant") {
      state.messages.splice(lastIdx, 1, { ...state.messages[lastIdx], ...data });
    }
  },
  SET_MESSAGES_LOADING(state, val) {
    state.messagesLoading = val;
  },

  // Lenses
  SET_LENSES(state, lenses) {
    state.lenses = lenses;
  },
  SET_ACTIVE_LENS(state, lens) {
    state.activeLens = lens;
  },
  ADD_LENS(state, lens) {
    state.lenses.unshift(lens);
  },
  REMOVE_LENS(state, id) {
    state.lenses = state.lenses.filter((l) => l.id !== id);
    if (state.activeLens && state.activeLens.id === id) {
      state.activeLens = null;
    }
  },
  SET_LENSES_LOADING(state, val) {
    state.lensesLoading = val;
  },

  // Mode
  SET_MODE(state, mode) {
    state.mode = mode;
  },
  SET_REPORT_TYPE(state, type) {
    state.reportType = type;
  },
  SET_REPORT_STEP(state, step) {
    state.reportStep = step;
  },

  // Report
  SET_REPORT_BRIEFING(state, briefing) {
    state.reportBriefing = briefing;
  },
  SET_REPORT_OUTLINE(state, outline) {
    state.reportOutline = outline;
  },
  SET_REPORT_SECTIONS(state, sections) {
    state.reportSections = sections;
  },
  UPDATE_REPORT_SECTION(state, { index, data }) {
    if (state.reportSections[index]) {
      state.reportSections.splice(index, 1, { ...state.reportSections[index], ...data });
    }
  },
  ADD_REPORT_SECTION_CONTENT(state, { index, content }) {
    if (state.reportSections[index]) {
      const section = state.reportSections[index];
      state.reportSections.splice(index, 1, {
        ...section,
        content: (section.content || "") + content,
      });
    }
  },
  SET_REPORT_EXEC_SUMMARY(state, summary) {
    state.reportExecSummary = summary;
  },

  // Gap analysis
  SET_GAP_QUESTIONS(state, questions) {
    state.gapQuestions = questions;
  },
  SET_GAP_RESULTS(state, results) {
    state.gapResults = results;
  },

  // Timeline
  SET_TIMELINE_EVENTS(state, events) {
    state.timelineEvents = events;
  },

  // Expansion
  SET_EXPANSION_BASELINE(state, baseline) {
    state.expansionBaseline = baseline;
  },
  SET_EXPANSION_RESULTS(state, results) {
    state.expansionResults = results;
  },

  // Streaming
  SET_STREAMING(state, val) {
    state.isStreaming = val;
  },
  SET_STREAMING_TEXT(state, text) {
    state.streamingText = text;
  },
  APPEND_STREAMING_TEXT(state, text) {
    state.streamingText += text;
  },
  SET_STREAMING_META(state, meta) {
    state.streamingMeta = meta;
  },
  SET_STREAM_CONTROLLER(state, controller) {
    state.streamController = controller;
  },
  CLEAR_STREAMING(state) {
    state.isStreaming = false;
    state.streamingText = "";
    state.streamingMeta = null;
    state.streamController = null;
  },

  // UI
  SET_SIDEBAR_COLLAPSED(state, val) {
    state.sidebarCollapsed = val;
  },
  TOGGLE_SIDEBAR(state) {
    state.sidebarCollapsed = !state.sidebarCollapsed;
  },
  SET_SLIDE_OUT(state, { open, article }) {
    state.slideOutOpen = open;
    state.slideOutArticle = article || null;
  },
  SET_SHARE_DIALOG(state, { open, target }) {
    state.shareDialogOpen = open;
    state.shareTarget = target || null;
  },

  // Artifacts
  SET_ARTIFACTS(state, artifacts) {
    state.artifacts = artifacts;
  },
  ADD_ARTIFACT(state, artifact) {
    state.artifacts.unshift(artifact);
  },

  // Error
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },

  // Reset
  RESET_CONVERSATION_STATE(state) {
    state.activeConversation = null;
    state.messages = [];
    state.streamingText = "";
    state.streamingMeta = null;
    state.isStreaming = false;
    state.reportBriefing = null;
    state.reportOutline = [];
    state.reportSections = [];
    state.reportExecSummary = "";
    state.reportStep = 0;
    state.reportType = null;
    state.gapQuestions = [];
    state.gapResults = null;
    state.timelineEvents = [];
    state.expansionBaseline = null;
    state.expansionResults = null;
    state.error = null;
  },
};

/**
 * Create a token refresh callback for streaming endpoints.
 * On 401, refreshes the token via Auth store and returns the new token.
 */
function makeStreamOptions(dispatch, rootState) {
  return {
    async onRefreshToken() {
      const refreshToken = rootState.Auth?.refresh_token;
      if (!refreshToken) return null;
      try {
        const result = await dispatch("Auth/refreshToken", {
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        }, { root: true });
        return (result && result.access_token) || rootState.Auth?.access_token || null;
      } catch (e) {
        return null;
      }
    },
  };
}

const actions = {
  // ─── Init ─────────────────────────────────────────────────
  initViq({ commit }) {
    if (process.client) {
      const enabled = window.drupalSettings?.viq?.enabled || false;
      commit("SET_ENABLED", enabled);
    }
  },

  // ─── Conversations ────────────────────────────────────────
  async fetchConversations({ commit }) {
    const api = createViqApi(this);
    commit("SET_CONVERSATIONS_LOADING", true);
    try {
      const data = await api.getConversations();
      commit("SET_CONVERSATIONS", data.conversations || data || []);
    } catch (err) {
      commit("SET_ERROR", "Failed to load conversations");
    } finally {
      commit("SET_CONVERSATIONS_LOADING", false);
    }
  },

  async loadConversation({ commit }, id) {
    const api = createViqApi(this);
    commit("SET_MESSAGES_LOADING", true);
    try {
      const data = await api.getConversation(id);
      commit("SET_ACTIVE_CONVERSATION", data.conversation || data);
      commit("SET_MESSAGES", data.messages || data.conversation?.messages || []);
    } catch (err) {
      commit("SET_ERROR", "Failed to load conversation");
    } finally {
      commit("SET_MESSAGES_LOADING", false);
    }
  },

  async createConversation({ commit }, payload) {
    const api = createViqApi(this);
    try {
      const data = await api.createConversation(payload);
      const conv = data.conversation || data;
      commit("ADD_CONVERSATION", conv);
      commit("SET_ACTIVE_CONVERSATION", conv);
      commit("SET_MESSAGES", []);
      return conv;
    } catch (err) {
      commit("SET_ERROR", "Failed to create conversation");
      return null;
    }
  },

  async renameConversation({ commit }, { id, title }) {
    const api = createViqApi(this);
    try {
      await api.updateConversation(id, { title });
      commit("UPDATE_CONVERSATION", { id, data: { title } });
    } catch (err) {
      commit("SET_ERROR", "Failed to rename conversation");
    }
  },

  async deleteConversation({ commit }, id) {
    const api = createViqApi(this);
    try {
      await api.deleteConversation(id);
      commit("REMOVE_CONVERSATION", id);
    } catch (err) {
      commit("SET_ERROR", "Failed to delete conversation");
    }
  },

  // ─── Ask (Q&A streaming) ──────────────────────────────────
  askQuestion({ commit, dispatch, state, rootState }, { question, conversationId, lensId }) {
    const api = createViqApi(this);

    // Set active conversation immediately so UI switches to chat panel
    if (!state.activeConversation) {
      const pendingConv = {
        id: conversationId || "pending-" + Date.now(),
        title: question.substring(0, 60),
        type: "ask",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      commit("SET_ACTIVE_CONVERSATION", pendingConv);
      commit("ADD_CONVERSATION", pendingConv);
    }

    // Add user message
    const userMsg = {
      id: "temp-" + Date.now(),
      role: "user",
      content: question,
      created_at: new Date().toISOString(),
    };
    commit("ADD_MESSAGE", userMsg);

    // Add placeholder assistant message — all fields must be defined for Vue 2 reactivity
    const assistantMsg = {
      id: "temp-assistant-" + Date.now(),
      role: "assistant",
      content: "",
      citations: [],
      sources: [],
      confidence: null,
      follow_ups: [],
      follow_up_questions: [],
      quick_replies: [],
      clarifying_questions: [],
      expert_commentary: [],
      expert_upsell: [],
      related_topics: [],
      created_at: new Date().toISOString(),
    };
    commit("ADD_MESSAGE", assistantMsg);

    commit("SET_STREAMING", true);
    commit("SET_STREAMING_TEXT", "");
    commit("SET_STREAMING_META", null);

    // Build conversation history from prior messages (exclude the ones we just added)
    const history = state.messages
      .filter((m) => m.id !== userMsg.id && m.id !== assistantMsg.id && m.content)
      .map((m) => ({ role: m.role, content: m.content }));

    const token = rootState.Auth?.access_token;
    const payload = { query: question };
    if (conversationId) payload.conversation_id = conversationId;
    if (lensId) payload.lens_id = lensId;
    if (history.length > 0) payload.history = history;

    const controller = api.stream("/ask/stream", payload, {
      onMeta(meta) {
        commit("SET_STREAMING_META", meta);
        // Update conversation with real ID from backend
        if (meta.conversation_id && state.activeConversation) {
          const oldId = state.activeConversation.id;
          const updated = {
            id: meta.conversation_id,
            title: meta.title || question.substring(0, 60),
            type: "ask",
            updated_at: new Date().toISOString(),
          };
          commit("SET_ACTIVE_CONVERSATION", updated);
          // Replace pending conversation in the list
          if (oldId !== meta.conversation_id) {
            commit("REMOVE_CONVERSATION", oldId);
            commit("ADD_CONVERSATION", updated);
          } else {
            commit("UPDATE_CONVERSATION", { id: meta.conversation_id, data: updated });
          }
        }
        commit("UPDATE_LAST_ASSISTANT_MESSAGE", {
          conversation_id: meta.conversation_id,
          sources: meta.sources || [],
          confidence: meta.confidence || null,
          follow_ups: meta.follow_ups || [],
          related_topics: meta.related_topics || [],
          expert_articles: meta.expert_articles || [],
          upsell_jurisdictions: meta.upsell_jurisdictions || [],
        });
      },
      onText(data) {
        const text = typeof data === "string" ? data : data.t || data.text || data.content || "";
        if (text) {
          commit("APPEND_STREAMING_TEXT", text);
          commit("UPDATE_LAST_ASSISTANT_MESSAGE", {
            content: state.streamingText,
          });
        }
      },
      onDone(data) {
        // Finalize message with complete data from Skywalker
        const finalData = typeof data === "object" ? data : {};
        // Filter out cross-vertical citations (FS content on PC platform)
        const pcVerticals = ["payments compliance", "payments_compliance", "payments"];
        const pcOnly = (arr) => (arr || []).filter((c) =>
          !c.vertical || pcVerticals.includes(c.vertical.toLowerCase())
        );
        commit("UPDATE_LAST_ASSISTANT_MESSAGE", {
          content: finalData.answer || state.streamingText,
          id: finalData.message_id || assistantMsg.id,
          citations: pcOnly(finalData.citations),
          confidence: finalData.confidence || null,
          follow_ups: finalData.follow_up_questions || finalData.follow_ups || [],
          quick_replies: finalData.quick_replies || [],
          clarifying_questions: finalData.clarifying_questions || [],
          expert_commentary: pcOnly(finalData.expert_commentary),
          expert_upsell: pcOnly(finalData.expert_upsell),
          related_topics: finalData.related_articles || [],
        });
        commit("CLEAR_STREAMING");
      },
      onError(err) {
        commit("SET_ERROR", err.message || "Streaming error");
        commit("UPDATE_LAST_ASSISTANT_MESSAGE", {
          content: "Sorry, something went wrong. Please try again.",
        });
        commit("CLEAR_STREAMING");
      },
    }, token, makeStreamOptions(dispatch, rootState));

    commit("SET_STREAM_CONTROLLER", controller);
  },

  cancelStream({ state, commit }) {
    if (state.streamController) {
      state.streamController.abort();
    }
    commit("CLEAR_STREAMING");
  },

  // ─── Upsell Interest ─────────────────────────────────────
  async recordUpsellInterest(_, { type, value, context }) {
    const api = createViqApi(this);
    try {
      await api.recordUpsellInterest(type, value, context);
    } catch (e) {
      // Silent fail — upsell tracking is non-critical
    }
  },

  // ─── Reports ──────────────────────────────────────────────
  async generateBriefing({ commit }, { topic, reportType }) {
    const api = createViqApi(this);
    try {
      const data = await api.reportBrief({ topic, type: reportType });
      commit("SET_REPORT_BRIEFING", data.briefing || data);
      commit("SET_REPORT_OUTLINE", data.outline || data.sections || []);
      commit("SET_REPORT_STEP", 2);
      return data;
    } catch (err) {
      commit("SET_ERROR", "Failed to generate briefing");
      return null;
    }
  },

  async refineReport({ commit }, { conversationId, instructions }) {
    const api = createViqApi(this);
    try {
      const data = await api.reportRefine({ conversation_id: conversationId, instructions });
      commit("SET_REPORT_OUTLINE", data.outline || data.sections || []);
      return data;
    } catch (err) {
      commit("SET_ERROR", "Failed to refine report");
      return null;
    }
  },

  streamReport({ commit, dispatch, state, rootState }, { conversationId, sections }) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    // Initialize sections
    const sectionStates = sections.map((s) => ({
      title: s.title || s,
      content: "",
      status: "pending",
    }));
    commit("SET_REPORT_SECTIONS", sectionStates);
    commit("SET_STREAMING", true);
    commit("SET_REPORT_STEP", 3);

    const controller = api.stream(
      "/report/stream",
      { conversation_id: conversationId, sections },
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
          if (meta.section_index !== undefined) {
            commit("UPDATE_REPORT_SECTION", {
              index: meta.section_index,
              data: { status: "streaming" },
            });
          }
        },
        onText(data) {
          const text = typeof data === "string" ? data : data.text || "";
          const idx = state.streamingMeta?.section_index || 0;
          commit("ADD_REPORT_SECTION_CONTENT", { index: idx, content: text });
        },
        onDone(data) {
          if (data && data.exec_summary) {
            commit("SET_REPORT_EXEC_SUMMARY", data.exec_summary);
          }
          // Mark all sections complete
          state.reportSections.forEach((_, idx) => {
            commit("UPDATE_REPORT_SECTION", {
              index: idx,
              data: { status: "complete" },
            });
          });
          commit("CLEAR_STREAMING");
          commit("SET_REPORT_STEP", 4);
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Report streaming error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  // ─── Timeline ─────────────────────────────────────────────
  streamTimeline({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_TIMELINE_EVENTS", []);

    const controller = api.stream(
      "/timeline/stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          if (data && data.event) {
            commit("SET_TIMELINE_EVENTS", [...(state?.timelineEvents || []), data.event]);
          }
        },
        onDone() {
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Timeline streaming error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  // ─── Gap Analysis ─────────────────────────────────────────
  streamGapQuestions({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_GAP_QUESTIONS", []);

    const controller = api.stream(
      "/gap-analysis/questions/stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          if (data && data.question) {
            const current = [...state?.gapQuestions || []];
            current.push(data.question);
            commit("SET_GAP_QUESTIONS", current);
          }
        },
        onDone(data) {
          if (data && data.questions) {
            commit("SET_GAP_QUESTIONS", data.questions);
          }
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Gap analysis error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  streamGapAnalysis({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_STREAMING_TEXT", "");

    const controller = api.stream(
      "/gap-analysis/stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          const text = typeof data === "string" ? data : data.text || "";
          commit("APPEND_STREAMING_TEXT", text);
        },
        onDone(data) {
          commit("SET_GAP_RESULTS", data || { content: state?.streamingText });
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Gap analysis error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  // ─── Expansion ────────────────────────────────────────────
  async fetchExpansionBaseline({ commit }, payload) {
    const api = createViqApi(this);
    try {
      const data = await api.expansionBaseline(payload);
      commit("SET_EXPANSION_BASELINE", data);
      return data;
    } catch (err) {
      commit("SET_ERROR", "Failed to fetch expansion baseline");
      return null;
    }
  },

  streamExpansion({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_STREAMING_TEXT", "");

    const controller = api.stream(
      "/expansion-stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          const text = typeof data === "string" ? data : data.text || "";
          commit("APPEND_STREAMING_TEXT", text);
        },
        onDone(data) {
          commit("SET_EXPANSION_RESULTS", data || { content: state?.streamingText });
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Expansion streaming error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  // ─── Lenses ───────────────────────────────────────────────
  async fetchLenses({ commit }) {
    const api = createViqApi(this);
    commit("SET_LENSES_LOADING", true);
    try {
      const data = await api.getLenses();
      commit("SET_LENSES", data.lenses || data || []);
    } catch (err) {
      commit("SET_ERROR", "Failed to load lenses");
    } finally {
      commit("SET_LENSES_LOADING", false);
    }
  },

  async deleteLens({ commit }, id) {
    const api = createViqApi(this);
    try {
      await api.deleteLens(id);
      commit("REMOVE_LENS", id);
    } catch (err) {
      commit("SET_ERROR", "Failed to delete lens");
    }
  },

  streamLensBuild({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_STREAMING_TEXT", "");

    const controller = api.stream(
      "/lenses/build/stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          const text = typeof data === "string" ? data : data.text || "";
          commit("APPEND_STREAMING_TEXT", text);
        },
        onDone(data) {
          if (data && data.lens) {
            commit("ADD_LENS", data.lens);
            commit("SET_ACTIVE_LENS", data.lens);
          }
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Lens build error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  streamLensRefine({ commit, dispatch, rootState }, payload) {
    const api = createViqApi(this);
    const token = rootState.Auth.access_token;

    commit("SET_STREAMING", true);
    commit("SET_STREAMING_TEXT", "");

    const controller = api.stream(
      "/lenses/refine/stream",
      payload,
      {
        onMeta(meta) {
          commit("SET_STREAMING_META", meta);
        },
        onText(data) {
          const text = typeof data === "string" ? data : data.text || "";
          commit("APPEND_STREAMING_TEXT", text);
        },
        onDone(data) {
          if (data && data.lens) {
            commit("SET_ACTIVE_LENS", data.lens);
          }
          commit("CLEAR_STREAMING");
        },
        onError(err) {
          commit("SET_ERROR", err.message || "Lens refine error");
          commit("CLEAR_STREAMING");
        },
      },
      token,
      makeStreamOptions(dispatch, rootState)
    );

    commit("SET_STREAM_CONTROLLER", controller);
  },

  // ─── Artifacts ────────────────────────────────────────────
  async fetchArtifacts({ commit }) {
    const api = createViqApi(this);
    try {
      const data = await api.getArtifacts();
      commit("SET_ARTIFACTS", data.artifacts || data || []);
    } catch (err) {
      commit("SET_ERROR", "Failed to load artifacts");
    }
  },

  async saveArtifact({ commit }, payload) {
    const api = createViqApi(this);
    try {
      const data = await api.createArtifact(payload);
      const artifact = data.artifact || data;
      commit("ADD_ARTIFACT", artifact);
      return artifact;
    } catch (err) {
      commit("SET_ERROR", "Failed to save artifact");
      return null;
    }
  },

  async deleteArtifact({ commit }, slug) {
    const api = createViqApi(this);
    try {
      await api.deleteArtifact(slug);
      commit("SET_ARTIFACTS", state.artifacts.filter((a) => a.slug !== slug));
    } catch (err) {
      commit("SET_ERROR", "Failed to delete artifact");
    }
  },

  // ─── Action Items ─────────────────────────────────────────
  async createActionItems(_, payload) {
    const api = createViqApi(this);
    try {
      return await api.createActionItems(payload);
    } catch (err) {
      return null;
    }
  },

  async extractActions(_, payload) {
    const api = createViqApi(this);
    try {
      return await api.extractActions(payload);
    } catch (err) {
      return null;
    }
  },

  async submitFeedback(_, payload) {
    const api = createViqApi(this);
    try {
      return await api.submitFeedback(payload);
    } catch (err) {
      return null;
    }
  },

  // ─── Articles ─────────────────────────────────────────────
  async fetchArticle({ commit }, id) {
    const api = createViqApi(this);
    try {
      const data = await api.getArticle(id);
      return data.article || data;
    } catch (err) {
      commit("SET_ERROR", "Failed to load article");
      return null;
    }
  },

  // ─── UI Actions ───────────────────────────────────────────
  setMode({ commit }, mode) {
    commit("SET_MODE", mode);
  },

  selectReportType({ commit }, type) {
    commit("SET_REPORT_TYPE", type);
    commit("SET_REPORT_STEP", 1);
  },

  toggleSidebar({ commit }) {
    commit("TOGGLE_SIDEBAR");
  },

  openSlideOut({ commit }, article) {
    commit("SET_SLIDE_OUT", { open: true, article });
  },

  closeSlideOut({ commit }) {
    commit("SET_SLIDE_OUT", { open: false, article: null });
  },

  openShareDialog({ commit }, target) {
    commit("SET_SHARE_DIALOG", { open: true, target });
  },

  closeShareDialog({ commit }) {
    commit("SET_SHARE_DIALOG", { open: false, target: null });
  },

  newConversation({ commit }) {
    commit("RESET_CONVERSATION_STATE");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
